/**
 * Google OAuth callback - processes #session_id= from Emergent Auth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { carrySnapshotIfAny } from "@/lib/snapshotCarryover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");

    (async () => {
      try {
        if (!sessionId) throw new Error("missing session_id");
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        const hasPending = !!localStorage.getItem("adcprep_pending_diagnostic");
        const isNew = !!res?.data?.is_new_user;
        // Carry the readiness snapshot into the new account (skips /welcome step 1)
        if (isNew) await carrySnapshotIfAny();
        // From the readiness results "Start your 10-minute plan" CTA -> go straight to the plan
        const planSubject = localStorage.getItem("adcprep_pending_diagnostic") ? null : localStorage.getItem("passadc_plan_subject");
        if (planSubject) {
          try { localStorage.removeItem("passadc_plan_subject"); } catch { /* ignore */ }
          navigate(`/plan/today?subject=${planSubject}`, { replace: true });
          return;
        }
        navigate(hasPending ? "/diagnostic" : (isNew ? "/welcome" : "/dashboard"), { replace: true });
      } catch (err) {
        window.history.replaceState(null, "", "/login");
        toast.error("Google sign-in failed", {
          description: err?.response?.data?.detail || "Please try again.",
        });
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
